import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, FloppyDisk, ChatCircleText, IdentificationCard, Cpu, Code, Lightning, Database, Rocket, CaretRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { AgentAvatar } from "@/components/agent/AgentCard";
import { ChatPanel } from "@/components/chat/ChatPanel";
import { IdentitySection } from "@/components/builder/IdentitySection";
import { ModelSection, InstructionsSection } from "@/components/builder/ModelSection";
import { SkillsSection } from "@/components/builder/SkillsSection";
import { TrainingSection } from "@/components/builder/TrainingSection";
import { PublishSection } from "@/components/builder/PublishSection";
import { api, errMsg } from "@/lib/api";
import { useMeta } from "@/lib/useMeta";

const FIELDS = ["name", "tagline", "description", "category", "icon", "color", "provider", "model", "system_prompt", "guardrails", "tone", "response_length", "language", "skill_ids", "conversation_starters", "use_knowledge"];
const TABS = [["identity", "Identidad", IdentificationCard], ["model", "Modelo", Cpu], ["instructions", "Instrucciones", Code], ["skills", "Skills", Lightning], ["training", "Entrenamiento", Database], ["publish", "Publicar", Rocket]];
const pick = (a) => Object.fromEntries(FIELDS.map((k) => [k, a[k] ?? (k === "skill_ids" || k === "conversation_starters" ? [] : k === "use_knowledge" ? true : "")]));

export default function Builder() {
  const { agentId } = useParams();
  const qc = useQueryClient();
  const { data: meta } = useMeta();
  const [agent, setAgent] = useState(null);
  const [form, setForm] = useState(null);
  const [tab, setTab] = useState("identity");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(() => api.get(`/agents/${agentId}`).then(({ data }) => { setAgent(data); setForm((f) => f || pick(data)); }).catch((e) => setError(errMsg(e))), [agentId]);
  useEffect(() => { load(); }, [load]);

  const dirty = agent && form && JSON.stringify(pick(agent)) !== JSON.stringify(form);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = useCallback(async (silent = false) => {
    if (!form.name.trim()) { toast.error("El nombre es obligatorio"); return false; }
    setSaving(true);
    try {
      const { data } = await api.put(`/agents/${agentId}`, { ...form, conversation_starters: form.conversation_starters.filter((s) => s.trim()) });
      setAgent(data);
      setForm(pick(data));
      qc.invalidateQueries({ queryKey: ["agents"] });
      if (!silent) toast.success("Cambios guardados");
      return true;
    } catch (e) { toast.error(errMsg(e)); return false; } finally { setSaving(false); }
  }, [form, agentId, qc]);

  useEffect(() => {
    const onKey = (e) => { if ((e.metaKey || e.ctrlKey) && e.key === "s") { e.preventDefault(); if (dirty) save(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dirty, save]);

  if (error) return <div className="min-h-screen bg-[#050505] p-12 text-white"><p className="font-display text-3xl font-bold">{error}</p><Link to="/agents" className="btn-ghost mt-6">Volver</Link></div>;
  if (!agent || !form || !meta) return <div className="min-h-screen bg-[#050505]"><div className="h-px bg-white/10 thinking-beam" /></div>;

  const ensureSaved = () => (dirty ? save(true) : true);
  const section = {
    identity: <IdentitySection form={form} set={set} meta={meta} />,
    model: <ModelSection form={form} set={set} meta={meta} />,
    instructions: <InstructionsSection form={form} set={set} />,
    skills: <SkillsSection form={form} set={set} />,
    training: <TrainingSection agent={agent} form={form} set={set} onChanged={load} />,
    publish: <PublishSection agent={agent} onUpdated={(a) => { setAgent(a); qc.invalidateQueries({ queryKey: ["agents"] }); }} ensureSaved={ensureSaved} />,
  }[tab];

  return (
    <div className="noise flex h-screen flex-col bg-[#050505] text-white">
      <header className="flex h-16 shrink-0 items-center justify-between gap-4 border-b border-white/10 bg-black/60 px-4 backdrop-blur-xl lg:px-6">
        <div className="flex min-w-0 items-center gap-3">
          <Link to="/agents" className="p-2 text-zinc-400 hover:text-white" data-testid="builder-back-button"><ArrowLeft size={18} /></Link>
          <AgentAvatar icon={form.icon} color={form.color} size={34} />
          <div className="min-w-0">
            <p className="truncate font-display font-bold" data-testid="builder-agent-name">{form.name || "Sin nombre"}</p>
            <p className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-zinc-500">
              <span className={`h-1.5 w-1.5 ${agent.published ? "bg-emerald-500" : "bg-zinc-600"}`} />{agent.published ? "Publicado" : "Borrador"} · v{agent.version}
              {dirty && <span className="text-amber-400" data-testid="builder-unsaved-indicator">· sin guardar</span>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to={`/chat/${agentId}`} className="btn-ghost hidden !py-2 md:inline-flex" data-testid="builder-open-chat-button"><ChatCircleText size={16} /> Chat completo</Link>
          <button onClick={() => save()} disabled={!dirty || saving} className="btn-primary !py-2" data-testid="builder-save-button"><FloppyDisk size={16} /> {saving ? "Guardando…" : "Guardar"}</button>
        </div>
      </header>
      <div className="flex min-h-0 flex-1">
        <nav className="hidden w-56 shrink-0 border-r border-white/10 bg-[#080808] p-3 md:block">
          {TABS.map(([id, l, I], n) => (
            <button key={id} onClick={() => setTab(id)} className={`group flex w-full items-center gap-3 border-l-2 px-3 py-2.5 text-left text-sm ${tab === id ? "border-blue-500 bg-white/5 text-white" : "border-transparent text-zinc-400 hover:text-white"}`} data-testid={`builder-tab-${id}`} style={{ transition: "color 160ms" }}>
              <span className="font-mono text-[10px] text-zinc-600">0{n + 1}</span><I size={17} weight="duotone" /><span className="flex-1">{l}</span>{tab === id && <CaretRight size={12} />}
            </button>
          ))}
        </nav>
        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="flex gap-1 overflow-x-auto border-b border-white/10 p-2 md:hidden">
            {TABS.map(([id, l]) => <button key={id} onClick={() => setTab(id)} className={`shrink-0 px-3 py-1.5 text-xs ${tab === id ? "bg-blue-600 text-white" : "text-zinc-400"}`} data-testid={`builder-mtab-${id}`}>{l}</button>)}
          </div>
          <div key={tab} className="reveal">{section}</div>
        </div>
        <aside className="hidden w-[420px] shrink-0 border-l border-white/10 xl:block 2xl:w-[480px]" data-testid="builder-test-panel">
          <ChatPanel agent={{ ...agent, ...form }} test title="Zona de pruebas · versión guardada" beforeSend={ensureSaved} />
        </aside>
      </div>
    </div>
  );
}
